'use client';
import React from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Mail, Loader2, Lock } from 'lucide-react';

interface EmailConfirmGateProps {
  children: React.ReactNode;
}

export default function EmailConfirmGate({ children }: EmailConfirmGateProps) {
  const { user, loading } = useAuth();
  const { t } = useLanguage();

  // Still loading auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Not logged in at all
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-5">
            <Lock size={28} className="text-muted-foreground" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">
            {t('Members only', '仅限会员')}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t('Please log in to access this page.', '请登录以访问此页面。')}
          </p>
          <Link href="/sign-up-login" className="btn-primary mx-auto">
            {t('Login / Register', '登录 / 注册')}
          </Link>
        </div>
      </div>
    );
  }

  // Logged in but email not confirmed
  if (!user.email_confirmed_at) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-5">
            <Mail size={28} className="text-accent" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">
            {t('Email not confirmed', '邮箱未确认')}
          </h2>
          <p className="text-sm text-muted-foreground mb-2">
            {t('Email not confirmed. Please verify your email first.', '邮箱未确认，请先验证您的邮箱。')}
          </p>
          <p className="text-xs text-muted-foreground mb-6">
            {t(
              'Check your inbox for the confirmation email and click the link to activate your account.',
              '请查收收件箱中的确认邮件，点击链接激活您的账户。'
            )}
          </p>
          <Link href="/sign-up-login" className="btn-primary mx-auto">
            {t('Back to Login', '返回登录')}
          </Link>
        </div>
      </div>
    );
  }

  // Email confirmed — allow access
  return <>{children}</>;
}
